import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Package, DollarSign, Activity, Clock, ArrowUp, ArrowDown, Zap, Target, Award, AlertCircle, BarChart, ShoppingCart, Factory, Truck, Calendar, CheckCircle } from 'lucide-react';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
// DISABLED: import '../utils/setupDashboardTables'; // NIE używamy auto-setup z danymi przykładowymi!

const EnhancedDashboardPage = () => {
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [liveData, setLiveData] = useState({
    production: 0,
    sales: 0,
    orders: 0,
    efficiency: 0,
    pendingOffers: 0,
    completedOrders: 0,
    activeClients: 0,
    monthlyRevenue: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch real production data
  const fetchProductionData = async () => {
    console.log('[Dashboard] Fetching production data...');
    try {
      // Today's production
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data: productionData } = await supabase
        .from('production_orders')
        .select('quantity, status')
        .gte('created_at', today.toISOString())
        .in('status', ['in_progress', 'completed']);
      
      const productionCount = productionData?.reduce((sum, order) => sum + order.quantity, 0) || 0;
      
      // Today's sales
      const { data: salesData } = await supabase
        .from('offers')
        .select('total_value, status')
        .gte('created_at', today.toISOString())
        .eq('status', 'accepted');
      
      const salesTotal = salesData?.reduce((sum, offer) => sum + (offer.total_value || 0), 0) || 0;
      
      // Today's orders
      const { data: ordersData } = await supabase
        .from('offers')
        .select('id')
        .gte('created_at', today.toISOString());
      
      const ordersCount = ordersData?.length || 0;
      
      // Pending offers
      const { data: pendingData } = await supabase
        .from('offers')
        .select('id')
        .eq('status', 'pending');
      
      const pendingCount = pendingData?.length || 0;
      
      // Completed orders this week
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      const { data: completedData } = await supabase
        .from('production_orders')
        .select('id')
        .gte('created_at', weekAgo.toISOString())
        .eq('status', 'completed');
      
      const completedCount = completedData?.length || 0;
      
      // Active clients
      const { data: clientsData } = await supabase
        .from('clients')
        .select('id, created_at')
        .gte('created_at', weekAgo.toISOString());
      
      const activeClientsCount = clientsData?.length || 0;
      
      // Monthly revenue
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      
      const { data: revenueData } = await supabase
        .from('offers')
        .select('total_value')
        .gte('created_at', monthAgo.toISOString())
        .eq('status', 'accepted');
      
      const monthlyRevenue = revenueData?.reduce((sum, offer) => sum + (offer.total_value || 0), 0) || 0;
      
      // Calculate efficiency (completed / total production)
      const { data: allProductionData } = await supabase
        .from('production_orders')
        .select('status')
        .gte('created_at', today.toISOString());
      
      const totalProduction = allProductionData?.length || 1;
      const completedProduction = allProductionData?.filter(o => o.status === 'completed').length || 0;
      const efficiency = (completedProduction / totalProduction) * 100;
      
      setLiveData({
        production: productionCount,
        sales: salesTotal,
        orders: ordersCount,
        efficiency: efficiency,
        pendingOffers: pendingCount,
        completedOrders: completedCount,
        activeClients: activeClientsCount,
        monthlyRevenue: monthlyRevenue
      });
    } catch (error) {
      console.error('[Dashboard] Error fetching production data:', error);
      console.error('[Dashboard] Error details:', {
        message: error.message,
        code: error.code,
        details: error.details
      });
      setHasError(true);
      setErrorMessage('Nie można pobrać danych. Sprawdź połączenie z bazą danych.');
      
      // NIE ustawiamy żadnych danych demo - tylko prawdziwe dane
      setLiveData({
        production: 0,
        sales: 0,
        orders: 0,
        efficiency: 0,
        pendingOffers: 0,
        completedOrders: 0,
        activeClients: 0,
        monthlyRevenue: 0
      });
    }
  };

  // Fetch recent activity
  const fetchRecentActivity = async () => {
    console.log('[Dashboard] Fetching recent activity...');
    try {
      const { data: activities } = await supabase
        .from('activity_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (activities && activities.length > 0) {
        setRecentActivity(activities.map(activity => ({
          time: formatTimeAgo(new Date(activity.created_at)),
          action: activity.action || activity.description || 'Akcja systemowa',
          user: activity.user_name || activity.user || 'System',
          type: activity.type || activity.level || 'info'
        })));
      } else {
        // Brak danych - pokazujemy puste
        setRecentActivity([]);
      }
    } catch (error) {
      console.error('[Dashboard] Error fetching activity:', error);
      console.error('[Dashboard] Activity error details:', {
        message: error.message,
        code: error.code
      });
      // Brak danych - pokazujemy puste
      setRecentActivity([]);
    }
  };

  // Fetch top products
  const fetchTopProducts = async () => {
    console.log('[Dashboard] Fetching top products...');
    try {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      
      const { data: productsData } = await supabase
        .from('production_orders')
        .select('product_type, quantity')
        .gte('created_at', monthAgo.toISOString())
        .eq('status', 'completed');
      
      if (productsData && productsData.length > 0) {
        // Aggregate by product type
        const productCounts = {};
        productsData.forEach(order => {
          const type = order.product_type || 'Inne';
          productCounts[type] = (productCounts[type] || 0) + order.quantity;
        });
        
        // Sort and take top 3
        const sorted = Object.entries(productCounts)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 3)
          .map(([name, count]) => ({ name, count }));
        
        setTopProducts(sorted);
      } else {
        // Brak danych - pokazujemy puste
        setTopProducts([]);
      }
    } catch (error) {
      console.error('Error fetching top products:', error);
    }
  };

  // Format time ago
  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Teraz';
    if (minutes < 60) return `${minutes} min`;
    if (hours < 24) return `${hours} godz`;
    return `${days} dni`;
  };

  // Initial data fetch
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchProductionData(),
        fetchRecentActivity(),
        fetchTopProducts()
      ]);
      setIsLoading(false);
    };
    
    fetchAllData();
    
    // Refresh data every 30 seconds
    const interval = setInterval(fetchAllData, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Update current time
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const greeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Dzień dobry';
    if (hour < 18) return 'Miłego popołudnia';
    return 'Dobry wieczór';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-500/20 border-t-violet-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Ładowanie danych...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Alert - No Mock Data */}
      {hasError && (
        <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <div>
              <p className="font-medium text-red-400">Błąd połączenia z bazą danych</p>
              <p className="text-sm text-gray-400 mt-1">
                {errorMessage || 'Nie można pobrać danych. Sprawdź połączenie z serwerem.'}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Header Section */}
      <div className="text-center lg:text-left">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
          {greeting()}, {user?.full_name || 'Użytkowniku'}! 👋
        </h1>
        <p className="text-gray-400">
          {currentTime.toLocaleDateString('pl-PL', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })} • {currentTime.toLocaleTimeString('pl-PL')}
        </p>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Production Card */}
        <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-black/40 to-black/20 backdrop-blur-xl border border-violet-500/20 hover:border-violet-500/40 transition-all duration-500 hover:scale-105 hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600/10 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-violet-500/20">
                <Package className="w-6 h-6 text-violet-400" />
              </div>
              <span className="flex items-center text-sm text-green-400 font-bold">
                <ArrowUp className="w-4 h-4 mr-1" />
                {liveData.production > 0 ? '+' : ''}{liveData.production}
              </span>
            </div>
            <h3 className="text-3xl font-bold mb-1 text-white">
              {liveData.production}
            </h3>
            <p className="text-sm text-gray-400">
              Produkcja dziś
            </p>
            <div className="mt-4 h-1 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-1000"
                style={{ width: `${Math.min(100, (liveData.production / 200) * 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Sales Card */}
        <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-black/40 to-black/20 backdrop-blur-xl border border-green-500/20 hover:border-green-500/40 transition-all duration-500 hover:scale-105 hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-r from-green-600/10 to-emerald-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-green-500/20">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
              {liveData.sales > 0 && (
                <span className="flex items-center text-sm text-green-400 font-bold">
                  <ArrowUp className="w-4 h-4 mr-1" />
                  {((liveData.sales / liveData.monthlyRevenue) * 100).toFixed(0)}%
                </span>
              )}
            </div>
            <h3 className="text-3xl font-bold mb-1 text-white">
              {liveData.sales.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} zł
            </h3>
            <p className="text-sm text-gray-400">
              Sprzedaż dziś
            </p>
            <div className="mt-4 h-1 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-1000"
                style={{ width: `${Math.min(100, (liveData.sales / 50000) * 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Orders Card */}
        <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-black/40 to-black/20 backdrop-blur-xl border border-blue-500/20 hover:border-blue-500/40 transition-all duration-500 hover:scale-105 hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-cyan-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-blue-500/20">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              {liveData.pendingOffers > 0 && (
                <span className="px-2 py-1 text-xs bg-yellow-500/20 text-yellow-400 rounded-full">
                  {liveData.pendingOffers} oczekuje
                </span>
              )}
            </div>
            <h3 className="text-3xl font-bold mb-1 text-white">
              {liveData.orders}
            </h3>
            <p className="text-sm text-gray-400">
              Nowe zamówienia
            </p>
            <div className="mt-4 h-1 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-1000"
                style={{ width: `${Math.min(100, (liveData.orders / 60) * 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Efficiency Card */}
        <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-black/40 to-black/20 backdrop-blur-xl border border-orange-500/20 hover:border-orange-500/40 transition-all duration-500 hover:scale-105 hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/10 to-red-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-orange-500/20">
                <Activity className="w-6 h-6 text-orange-400" />
              </div>
              <Zap className="w-4 h-4 text-yellow-400 animate-pulse" />
            </div>
            <h3 className="text-3xl font-bold mb-1 text-white">
              {liveData.efficiency.toFixed(1)}%
            </h3>
            <p className="text-sm text-gray-400">
              Wydajność
            </p>
            <div className="mt-4 h-1 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all duration-1000"
                style={{ width: `${liveData.efficiency}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-black/40 backdrop-blur-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-xs text-gray-400">Ukończone (7 dni)</span>
          </div>
          <p className="text-2xl font-bold text-white">{liveData.completedOrders}</p>
        </div>
        
        <div className="p-4 rounded-xl bg-black/40 backdrop-blur-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-gray-400">Aktywni klienci</span>
          </div>
          <p className="text-2xl font-bold text-white">{liveData.activeClients}</p>
        </div>
        
        <div className="p-4 rounded-xl bg-black/40 backdrop-blur-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-violet-400" />
            <span className="text-xs text-gray-400">Przychód (30 dni)</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {(liveData.monthlyRevenue / 1000).toFixed(0)}k zł
          </p>
        </div>
        
        <div className="p-4 rounded-xl bg-black/40 backdrop-blur-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-orange-400" />
            <span className="text-xs text-gray-400">Oczekujące</span>
          </div>
          <p className="text-2xl font-bold text-white">{liveData.pendingOffers}</p>
        </div>
      </div>

      {/* Activity Timeline & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Timeline */}
        <div className="lg:col-span-2 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 p-6">
          <h2 className="text-xl font-bold mb-4 text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-violet-400" />
            Ostatnia aktywność
          </h2>
          <div className="space-y-4">
            {recentActivity.map((item, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full animate-pulse
                    ${item.type === 'success' ? 'bg-green-400' : 
                      item.type === 'warning' ? 'bg-yellow-400' : 
                      item.type === 'error' ? 'bg-red-400' : 'bg-blue-400'}`} 
                  />
                  <div>
                    <p className="font-medium text-white">
                      {item.action}
                    </p>
                    <p className="text-sm text-gray-400">
                      {item.user}
                    </p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">
                  {item.time}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions & Top Products */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 p-6">
            <h2 className="text-xl font-bold mb-4 text-white">
              Szybkie akcje
            </h2>
            <div className="space-y-3">
              {[
                { icon: Package, label: 'Nowe zamówienie', color: 'violet', href: '/production' },
                { icon: Users, label: 'Dodaj klienta', color: 'blue', href: '/clients' },
                { icon: TrendingUp, label: 'Generuj raport', color: 'green', href: '/reports' },
                { icon: Target, label: 'Cele sprzedaży', color: 'orange', href: '/profitability' },
              ].map((action, i) => (
                <a
                  key={i}
                  href={action.href}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all text-white"
                >
                  <action.icon className={`w-5 h-5 text-${action.color}-400`} />
                  <span className="font-medium">{action.label}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Top Products */}
          <div className="rounded-2xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 p-6">
            <h3 className="font-bold text-white mb-3 flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-400" />
              Top produkty
            </h3>
            <div className="space-y-2">
              {topProducts.map((product, i) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-sm text-gray-300">{product.name}</span>
                  <span className="text-sm font-bold text-purple-400">{product.count} szt</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* AI Insights */}
      <div className="rounded-2xl bg-gradient-to-r from-violet-500/10 to-purple-500/10 backdrop-blur-xl border border-violet-500/20 p-6">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-5 h-5 text-violet-400" />
          <h2 className="text-xl font-bold text-white">
            AI Insights - Analiza produkcyjna
          </h2>
        </div>
        <div className="text-gray-300 space-y-2">
          {liveData.sales > 10000 ? (
            <p>📈 Świetny dzień! Sprzedaż przekroczyła {Math.floor(liveData.sales / 1000)}k zł. To {((liveData.sales / (liveData.monthlyRevenue / 30)) * 100).toFixed(0)}% średniej dziennej.</p>
          ) : (
            <p>📊 Sprzedaż wynosi {liveData.sales.toFixed(0)} zł. Rozważ promocję lub kontakt z kluczowymi klientami.</p>
          )}
          
          {liveData.efficiency > 90 ? (
            <p>⚡ Wydajność produkcji na poziomie {liveData.efficiency.toFixed(1)}% - doskonały wynik!</p>
          ) : liveData.efficiency > 70 ? (
            <p>⚙️ Wydajność {liveData.efficiency.toFixed(1)}% - w normie, ale jest miejsce na optymalizację.</p>
          ) : (
            <p>⚠️ Wydajność tylko {liveData.efficiency.toFixed(1)}% - sprawdź procesy produkcyjne.</p>
          )}
          
          {liveData.pendingOffers > 5 && (
            <p>🔔 Masz {liveData.pendingOffers} oczekujących ofert. Priorytetowo zajmij się najstarszymi.</p>
          )}
          
          {topProducts[0] && (
            <p>🏆 Najpopularniejszy produkt: {topProducts[0].name} ({topProducts[0].count} szt). Upewnij się, że masz wystarczające zapasy materiałów.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedDashboardPage;